import {
  track as rawTrack,
  identify,
  resetAnalytics,
  setProfileProps,
} from './mixpanel'

/**
 * Typed analytics layer for the PWA. Feature code imports event names and the
 * `track` helper from here — never a string literal, and never `mixpanel-browser`
 * directly (see `mixpanel.ts`). The canonical taxonomy is documented in the
 * repo-root `ANALYTICS.md`; this registry and the Swift enum
 * (`apps/swift/Sources/Caliu/App/AnalyticsEvent.swift`) must mirror it 1:1 —
 * change all three together or none. `analytics.test.ts` guards the parity.
 *
 * Both registries carry the FULL event set even when an event only fires on one
 * platform, so the name lists stay identical across clients. `platform` (web) is
 * attached automatically by `mixpanel.ts`, so no event sends it.
 */
export const AnalyticsEvent = {
  // Auth & lifecycle
  AppLaunched: 'app_launched',
  AppBootstrapAuthenticated: 'app_bootstrap_authenticated',
  UserLoggedIn: 'user_logged_in',
  SignUpCompleted: 'sign_up_completed',
  UserLoggedOut: 'user_logged_out',
  UserSessionExpired: 'user_session_expired',
  LocalModeStarted: 'local_mode_started',
  LocalModeUpgradePrompted: 'local_mode_upgrade_prompted',

  // Onboarding
  OnboardingStarted: 'onboarding_started',
  OnboardingCompleted: 'onboarding_completed',
  OnboardingSyncStepViewed: 'onboarding_sync_step_viewed',

  // Notes
  NoteCreated: 'note_created',
  NoteOpened: 'note_opened',
  NoteEdited: 'note_edited',
  NoteAction: 'note_action',

  // Editor / toolbar
  EditorFormatApplied: 'editor_format_applied',
  EditorTableAction: 'editor_table_action',
  EditorModeToggled: 'editor_mode_toggled',
  EditorFindOpened: 'editor_find_opened',
  NoteStatsViewed: 'note_stats_viewed',
  EditorToolbarCustomized: 'editor_toolbar_customized',

  // Tags
  TagCreated: 'tag_created',
  TagAction: 'tag_action',

  // Search & navigation
  CommandPaletteOpened: 'command_palette_opened',
  SearchPerformed: 'search_performed',
  SmartViewSelected: 'smart_view_selected',

  // Settings
  ThemeChanged: 'theme_changed',
  FontChanged: 'font_changed',
  LanguageChanged: 'language_changed',
  DisplayModeChanged: 'display_mode_changed',
  SettingChanged: 'setting_changed',
  AppIconChanged: 'app_icon_changed',

  // Attachments / share / export
  AttachmentAdded: 'attachment_added',
  NoteExported: 'note_exported',
  NoteShareCreated: 'note_share_created',
  NoteShareRevoked: 'note_share_revoked',
  ShareLinkCopied: 'share_link_copied',
  InboundShareIngested: 'inbound_share_ingested',
  ShareReconciled: 'share_reconciled',

  // Sharing
  ShareSheetOpened: 'share_sheet_opened',
  NoteInviteSent: 'note_invite_sent',
  NoteMemberRoleChanged: 'note_member_role_changed',
  NoteLinkAccessChanged: 'note_link_access_changed',
  SharedNoteJoined: 'shared_note_joined',
  SharedNoteLeft: 'shared_note_left',

  // Paywall & purchase
  PaywallShown: 'paywall_shown',
  PaywallDismissed: 'paywall_dismissed',
  CheckoutStarted: 'checkout_started',
  CheckoutCompleted: 'checkout_completed',
  CheckoutCancelled: 'checkout_cancelled',
  CheckoutFailed: 'checkout_failed',
  PurchasesRestored: 'purchases_restored',
  RedeemCodeOpened: 'redeem_code_opened',
  ProStatusChanged: 'pro_status_changed',

  // Markdown vault (local files, macOS)
  VaultConnected: 'vault_connected',
  VaultDisconnected: 'vault_disconnected',
  VaultExternalEdit: 'vault_external_edit',
  VaultAttachmentsMirrored: 'vault_attachments_mirrored',

  // Platform-specific extras
  PwaInstalled: 'pwa_installed',
  ConnectorApproved: 'connector_approved',
  ConnectorRevoked: 'connector_revoked',
  UnexpectedError: 'unexpected_error',
} as const

export type AnalyticsEventName =
  (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent]

/** The full set of canonical event names — used by the parity test. */
export const ANALYTICS_EVENT_NAMES: readonly AnalyticsEventName[] =
  Object.values(AnalyticsEvent)

/**
 * Track a product event. Name must come from `AnalyticsEvent`. No-op until
 * Mixpanel is initialized with a token (dev builds never send).
 */
export function track(
  event: AnalyticsEventName,
  properties?: Record<string, unknown>,
): void {
  rawTrack(event, properties)
}

export { identify, resetAnalytics, setProfileProps }
